/** Small formatting helpers shared across components. No floating-point money math lives here. */

export function shortAddress(address: string, lead = 6, trail = 4): string {
  if (address.length <= lead + trail + 2) return address;
  return `${address.slice(0, lead)}…${address.slice(-trail)}`;
}

export function explorerAddressUrl(explorerUrl: string, address: string): string {
  return `${explorerUrl.replace(/\/+$/, "")}/address/${address}`;
}

export function explorerTxUrl(explorerUrl: string, txHash: string): string {
  return `${explorerUrl.replace(/\/+$/, "")}/tx/${txHash}`;
}

export function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export interface Countdown {
  totalMs: number;
  expired: boolean;
  label: string;
}

/** Renders a human countdown ("2d 4h left" / "Expired") without any float-based money math. */
export function countdownTo(deadlineIso: string, now: Date = new Date()): Countdown {
  const deadline = new Date(deadlineIso).getTime();
  const totalMs = deadline - now.getTime();
  if (totalMs <= 0) {
    return { totalMs, expired: true, label: "Deadline passed" };
  }
  const totalSeconds = Math.floor(totalMs / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (days > 0 || hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return { totalMs, expired: false, label: `${parts.join(" ")} left` };
}

export function formatDurationSeconds(totalSeconds: number): string {
  if (totalSeconds >= 86_400 && totalSeconds % 3_600 === 0) {
    const days = totalSeconds / 86_400;
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  if (totalSeconds >= 3_600) {
    const hours = Math.round(totalSeconds / 3_600);
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  const minutes = Math.max(1, Math.round(totalSeconds / 60));
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

export function truncateFilename(name: string, max = 48): string {
  if (name.length <= max) return name;
  const extIndex = name.lastIndexOf(".");
  const ext = extIndex > 0 ? name.slice(extIndex) : "";
  const base = extIndex > 0 ? name.slice(0, extIndex) : name;
  const keep = Math.max(1, max - ext.length - 1);
  return `${base.slice(0, keep)}…${ext}`;
}
